import {Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {environment} from '../environments/environment';
import {SKIP_ERROR_INTERCEPTOR} from '../app/interceptors/error.interceptor';
import {Lead} from '../models/lead';
import {StorageService} from './storage-service';

const API = environment.API;

@Injectable({
  providedIn: 'root'
})
export class LeadService {

  constructor(private http: HttpClient,
              private storage: StorageService) {
  }

  generateReference(prefix: string) {
    prefix = prefix ? prefix + ':' : '';
    const ref = this.storage.get(StorageService.REF) ? 'ref_' + this.storage.get(StorageService.REF) + ':' : '';
    const productSlug = this.storage.get(StorageService.PRODUCT_SLUG) ? 'product_' + this.storage.get(StorageService.PRODUCT_SLUG) + ':' : '';
    const promoCode = this.storage.get(StorageService.PROMOTION_CODE) ? 'promo_' + this.storage.get(StorageService.PROMOTION_CODE) + ':' : '';
    const region = this.storage.get(StorageService.REGION_SLUG) ? 'region_' + this.storage.get(StorageService.REGION_SLUG) + ':' : '';

    return prefix + ref + productSlug + promoCode + region;
  }

  isCompanyEmail(email: string): boolean {
    const personalEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'me.com', 'mac.com', 'msn.com',
      'ymail.com', 'rocketmail.com', 'att.net', 'sbcglobal.net',
      'verizon.net', 'mail.com', 'cox.net', 'rediffmail.com',
      'yandex.com', 'mail.ru', 'qq.com', '163.com', '126.com',
      'sina.com', 'gmx.com', 'gmx.net', 'gmx.de', 'gmx.at',
      'web.de', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.it',
      'yahoo.es', 'yahoo.co.jp', 'yahoo.com.br', 'yahoo.com.au',
      'yahoo.com.ar', 'yahoo.com.mx', 'yahoo.ca', 'protonmail.com',
      'pm.me', 'zoho.com', 'fastmail.com', 'tutanota.com'
    ];

    const emailDomain = email.toLowerCase().split('@')[1];
    return !!(emailDomain && !personalEmailProviders.includes(emailDomain));
  }

  createRegionLead(email: string, ref: string, firstName: string, lastName: string, region: string) {
    const body = {email, ref, firstName, lastName, region};
    return this.http.post<Lead>(`${API}/api/leads/region`, body);
  }

  createPostalCodeLead(email: string, ref: string, firstName: string, lastName: string, postalCode: string, country: string) {
    const body = {email, ref, firstName, lastName, postalCode, country};
    return this.http.post<Lead>(`${API}/api/leads/postalcode`, body);
  }

  createCompanyPartialLead(email: string, ref: string, firstName: string, lastName: string, postalCode: string, country: string) {
    const body = {email, ref, firstName, lastName, postalCode, country};
    return this.http.post<Lead>(`${API}/api/leads/company`, body);
  }

  createCompanyLead(email: string, ref: string, firstName: string, lastName: string, companyName: string, companySize: string, companyUsage: string, postalCode: string, country: string) {
    const body = {email, ref, firstName, lastName, companyName, companySize, companyUsage, postalCode, country};
    return this.http.post<Lead>(`${API}/api/leads/company`, body);
  }

  createReferralLead(email: string, ref: string) {
    const body = {email, ref};
    return this.http.post<Lead>(`${API}/api/leads/referral`, body);
  }

  createAffiliateLead(email: string, ref: string) {
    const body = {email, ref};
    return this.http.post<Lead>(`${API}/api/leads/affiliate`, body);
  }

  getById(id: string) {
    return this.http.get<Lead>(`${API}/api/leads/${id}`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  findLeads(limit: number, skip: number) {
    const params = {limit, skip};
    return this.http.get<Lead[]>(`${API}/api/leads`, {params});
  }

  subscribe(email: string, ref: string) {
    const body = {email, ref};
    return this.http.post(`${API}/api/leads/subscribe`, body);
  }

  guide(email: string, ref: string, postalCode: string, country: string) {
    const body = {email, ref, postalCode, country};
    return this.http.post(`${API}/api/leads/guide`, body);
  }

  rsvp(email: string, ref: string, firstName: string, lastName: string) {
    const body = {email, ref, firstName, lastName};
    return this.http.post(`${API}/api/leads/rsvp`, body);
  }

  partner(fullName: string, email: string, phoneNumber: string, address: string) {
    const body = {fullName, email, phoneNumber, address};
    return this.http.post(`${API}/api/leads/partner`, body);
  }

  leaving(email: string, ref: string) {
    const body = {email, ref};
    return this.http.post(`${API}/api/leads/leaving`, body);
  }

  updateRegion(leadId: string, region: string) {
    const body = {region};
    return this.http.put(`${API}/api/leads/${leadId}/region`, body);
  }

}
